import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useSeo, { SITE_ORIGIN } from "@/hooks/useSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { rankBoards, rankTabs } from "@/mocks/rank";
import { formatHeat } from "@/utils/format";
import type { RankItem } from "@/types/content";

const trendMeta: Record<string, { icon: string; className: string; label: string }> = {
  up: { icon: "ri-arrow-up-line", className: "text-primary-600", label: "上升" },
  down: { icon: "ri-arrow-down-line", className: "text-foreground-400", label: "回落" },
  flat: { icon: "ri-subtract-line", className: "text-foreground-400", label: "持平" },
};

type TabKey = "realtime" | "today" | "week";

export default function Rank() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") as TabKey) || "realtime";
  const [tab, setTab] = useState<TabKey>(rankTabs.some((t) => t.key === initialTab) ? initialTab : "realtime");
  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");

  const list: RankItem[] = rankBoards[tab];
  const activeKeyword = (searchParams.get("q") ?? "").trim();
  const filtered = activeKeyword ? list.filter((item) => item.title.includes(activeKeyword)) : list;
  const maxHeat = Math.max(...((filtered.length ? filtered : list) as RankItem[]).map((item) => item.heat));

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "网爆吃瓜实时热榜",
      url: `${SITE_ORIGIN}/rank`,
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: list.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
          url: `${SITE_ORIGIN}/article/${item.id}`,
        })),
      },
    }),
    [list]
  );

  useSeo({
    title: "网爆吃瓜实时热榜 | 现场直播与秒删截图热度排行",
    description:
      "网爆吃瓜实时热榜按公开讨论量排序，实时热度、今日榜单、本周盘点三档切换，快速定位正在被讨论的网爆吃瓜现场直播与秒删截图话题。",
    keywords: "网爆吃瓜热榜,网爆吃瓜实时楼,网爆吃瓜现场直播,网爆吃瓜第一手爆料",
    path: "/rank",
    jsonLd,
  });

  const handleTab = (key: TabKey) => {
    setTab(key);
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  const handleSearch = (value: string) => {
    setKeyword(value);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) {
      next.set("q", value.trim());
    } else {
      next.delete("q");
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "热榜" }]} />

      <header className="flex flex-col gap-5 border-b border-background-200 pb-6 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
            网爆吃瓜实时热榜
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            榜单依据公开渠道的讨论量每小时刷新，覆盖网爆吃瓜现场直播、网爆吃瓜秒删截图与网爆吃瓜实时楼等话题类型。热度只是参考，不代表任何真实性判断。
          </p>
        </div>
        <div className="flex w-full items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 md:w-72">
          <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
          <input
            type="search"
            value={keyword}
            onChange={(event) => handleSearch(event.target.value)}
            placeholder="搜索榜单话题"
            aria-label="搜索榜单话题"
            className="w-full bg-transparent font-label text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
        </div>
      </header>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <div className="inline-flex items-center rounded-full bg-background-100 p-1" role="tablist" aria-label="榜单类型">
          {rankTabs.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={tab === item.key}
              onClick={() => handleTab(item.key as TabKey)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 font-label text-sm transition-colors ${
                tab === item.key
                  ? "bg-primary-500 font-medium text-background-50"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        <span className="font-label text-xs text-foreground-500">
          {activeKeyword ? `包含「${activeKeyword}」的 ${filtered.length} 条结果` : `共 ${list.length} 条话题`}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-10 rounded-lg border border-background-200 bg-background-100 px-6 py-14 text-center">
          <i className="ri-search-eye-line text-3xl text-foreground-400" aria-hidden="true"></i>
          <p className="mt-3 font-label text-sm text-foreground-700">没有找到匹配的话题</p>
          <button
            type="button"
            onClick={() => handleSearch("")}
            className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            清空搜索
          </button>
        </div>
      ) : (
        <ol className="mt-6 flex flex-col gap-3">
          {filtered.map((item, index) => {
            const trend = trendMeta[item.trend];
            const width = Math.max(12, Math.round((item.heat / maxHeat) * 100));
            return (
              <li key={`${tab}-${item.id}`}>
                <Link
                  to={`/article/${item.id}`}
                  className="group flex cursor-pointer flex-col gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-4 transition-colors hover:border-primary-300 md:flex-row md:items-center md:gap-5"
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-base font-bold ${
                      index < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                    }`}
                  >
                    {index + 1}
                  </span>

                  <div className="min-w-0 flex-1">
                    <h2 className="text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                      {item.title}
                    </h2>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                      <span className="block h-full rounded-full bg-primary-500" style={{ width: `${width}%` }} />
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-4">
                    <span className="rounded-full bg-secondary-100 px-2.5 py-1 font-label text-xs text-secondary-800">
                      {item.tag}
                    </span>
                    <span className="flex w-24 items-center gap-1 font-label text-sm text-foreground-700">
                      <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                      {formatHeat(item.heat)}
                    </span>
                    <span className={`flex items-center gap-0.5 font-label text-xs ${trend.className}`}>
                      <i className={trend.icon} aria-hidden="true"></i>
                      {trend.label}
                    </span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      )}

      <section className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
        <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">榜单是怎么排出来的</h2>
        <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-3">
          <div>
            <h3 className="font-label text-sm font-semibold text-primary-700">公开讨论量</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
              统计各公开讨论区中话题被提及的次数与参与人数，按小时汇总。
            </p>
          </div>
          <div>
            <h3 className="font-label text-sm font-semibold text-primary-700">增速权重</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
              短时间内快速上升的话题会获得额外权重，避免老话题长期占据前排。
            </p>
          </div>
          <div>
            <h3 className="font-label text-sm font-semibold text-primary-700">人工剔除</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
              涉及个人隐私或未经证实指控的话题，会在进入榜单前被人工剔除。
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}